import type { HelpTopic } from '../types';

export const proposalsTopics: HelpTopic[] = [
  {
    id: 'wms.proposals.create',
    title: 'Create a Proposal',
    icon: 'ri-file-text-line',
    summary: 'Draft a proposal that can later lead to one or more works and an admin sanction.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              A proposal captures a tentative scope and estimated cost before a work is formally sanctioned. Many proposals
              can later be bundled under a single admin sanction, and a proposal can be split into multiple works.
            </p>
          </div>
        )
      },
      {
        id: 'steps',
        title: 'Steps',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open <span className="font-medium">Proposals → New Proposal</span>.</li>
            <li>Choose what is being procured (Works / Goods / Services) and enter the proposal name, type, source and UDA.</li>
            <li>Pick work type / category, budget details (funding agency, department, budget head, scheme, FY) and location.</li>
            <li>Enter the <span className="font-medium">estimated cost</span>, priority and justification, then save. It is saved as a <span className="font-medium">Draft</span>.</li>
            <li>Click <span className="font-medium">Submit</span> and pick the officer it goes to. Name, estimated cost and work type (for Works) are required to submit.</li>
            <li>The reviewer clicks <span className="font-medium">Verify</span>, then the approver clicks <span className="font-medium">Approve</span> or <span className="font-medium">Reject</span> (a reason is required).</li>
            <li>Once <span className="font-medium">Approved</span>, open the proposal and click <span className="font-medium">Convert to Work</span> (one work) or <span className="font-medium">Create N works</span> (several works).</li>
          </ol>
        )
      },
      {
        id: 'states',
        title: 'Status flow',
        body: (
          <ul className="text-xs text-gray-600 space-y-1">
            <li>Draft → Submitted → Under Review → Approved → Converted to Work.</li>
            <li>Rejected can be set while Submitted or Under Review; the creator can <span className="font-medium">Reopen</span> it back to Draft.</li>
            <li>Only the officer a proposal is pending with can act on it; others see "Pending with …". The history icon shows every step.</li>
          </ul>
        )
      },
      {
        id: 'multiple-works',
        title: 'One proposal, several works',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              In the proposal form, use <span className="font-medium">Works in this proposal → Add work</span> to list each work
              with its own name, location and allocated amount. After approval, <span className="font-medium">Create N works</span> creates
              all of them in one go: each work is linked to the proposal, and the works are grouped into one work package so they can
              be tendered together.
            </p>
            <p>
              An existing work can also be linked to one or more proposals from the <span className="font-medium">Estimate</span> or
              <span className="font-medium"> Design</span> form (linked proposals).
            </p>
          </div>
        )
      },
      {
        id: 'tips',
        title: 'Tips',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>Keep the proposal name concise — it's shown in every downstream picker.</li>
            <li>Set UDA/Scheme on the proposal so estimates and works can inherit them.</li>
          </ul>
        )
      }
    ],
    related: ['wms.estimates.create', 'wms.as.create-from-unsanctioned-works'],
    pageLinks: [
      { label: 'Open Proposals', to: '/app/ds-wms/proposals', icon: 'ri-file-text-line' }
    ],
    launchForms: [
      { label: 'New Proposal', icon: 'ri-add-line', formKey: 'ProposalForm' }
    ]
  }
];
